<template>
  <div
    v-editable="blok"
    class="relative w-full overflow-x-clip"
    :style="{
      '--w': `${characterWidthWh}vw`,
      '--w-wrap': `${characterWidthWh * spacing}vw`,
      '--x-offset': `calc(-50% + var(--w-wrap) / 2)`,
    }">
    <div>
      <div class="module-pt module-pb">
        <ElementTexts
          v-if="blok.texts?.length"
          :bloks="blok.texts"
          textPosition="center"
          class="page-px clamp-mb-4-8" />
        <ElementCarousel
          :id="blok._uid"
          :itemCount="blok.characters.length"
          spacing="none"
          startPosition="center"
          :autoScroll="true">
          <div class="relative">
            <div
              class="flex flex-row items-center justify-center clamp-px-32-52 overflow-x-clip">
              <!-- character media -->
              <template v-for="item in blok.characters">
                <div
                  v-if="item.component === 'ElementCharacter'"
                  class="scroll-snap-item w-[--w-wrap]"
                  :class="{
                    'z-character-in-front': item.placeInFront,
                    'pointer-events-none': !isStoryblokVisualEditor,
                  }"
                  v-editable="item">
                  <ElementCharacterMedia
                    v-if="item !== undefined"
                    class="w-[--w] translate-x-[--x-offset] pointer-events-none"
                    :blok="item" />
                </div>
                <div
                  v-if="item.component === 'ElementCharacterSpace'"
                  class="pointer-events-none scroll-snap-item w-[--w-wrap-space]"
                  :style="{
                    '--w-wrap-space': `calc(var(--w-wrap) * ${
                      item.spacing.value || 0.5
                    })`,
                  }" />
              </template>
            </div>
            <!-- character tooltip - placed separatly from media to make z-index work -->
            <div
              :class="[
                'absolute top-0 left-0 bottom-0 z-character-tooltip pointer-events-none',
                'flex flex-row items-center justify-center clamp-px-32-52 overflow-x-clip',
              ]">
              <template v-for="item in blok.characters">
                <div
                  v-if="item.component === 'ElementCharacter'"
                  class="scroll-snap-item w-[--w-wrap] relative block h-full">
                  <div
                    class="tooltip w-[--w] translate-x-[--x-offset] h-full"
                    v-if="item.texts?.length">
                    <ElementCharacterTooltip
                      :blok="item"
                      :showDetails="showDetailsUid === item._uid"
                      @toggle-details="toggleDetails(item._uid)">
                      <ElementTexts
                        :bloks="item.texts"
                        textPosition="center"
                        gap="xs"
                        buttonSize="xs"
                        buttonVariant="secondary" />
                    </ElementCharacterTooltip>
                  </div>
                </div>
                <div
                  v-if="item.component === 'ElementCharacterSpace'"
                  class="scroll-snap-item w-[--w-wrap-space] h-full pointer-events-none"
                  :class="{
                    'b-1 b-dashed b-[--section-text-color] opacity-40':
                      isStoryblokVisualEditor,
                  }"
                  :style="{
                    '--w-wrap-space': `calc(var(--w-wrap) * ${
                      item.spacing.value || 0.5
                    })`,
                  }" />
              </template>
            </div>
          </div>
        </ElementCarousel>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useIsStoryblok from "@/composables/useIsStoryblok";
import { ModuleCharactersStoryblok } from "@/types";
import { useWindowSize } from "@vueuse/core";
import { getBreakpoint } from "@/../uno-preset/preset-theme";

const { isStoryblokVisualEditor } = useIsStoryblok();
const { width: windowWidth } = useWindowSize();

interface Props {
  blok: ModuleCharactersStoryblok;
}
const props = defineProps<Props>();

const spacing = computed(() => {
  return props.blok.spacing?.value || 0;
});

const charactersPerScreen = computed(() => {
  let count = 1;
  if (windowWidth.value > getBreakpoint("mpl")) count = 2;
  if (windowWidth.value > getBreakpoint("tp")) count = 2;
  if (windowWidth.value > getBreakpoint("tl")) count = 3;
  if (windowWidth.value > getBreakpoint("dl")) count = 4;
  return count;
});
const characterWidthWh = computed(() => {
  return 100 / charactersPerScreen.value;
});

const showDetailsUid = ref<string | null>(null);
const toggleDetails = (uid: string) => {
  if (showDetailsUid.value === uid) showDetailsUid.value = null;
  else showDetailsUid.value = uid;
};
</script>

<style lang="scss" scoped>
.tooltip {
  &:deep(p),
  &:deep(ol),
  &:deep(ul) {
    @apply text-body-xs;
    @apply not-last:clamp-mb-1-1 not-first:clamp-mt-1-1;
  }
}
</style>
